// The Bag and the Merchant. The Bag opens on its pockets (#/bag): a header card and one big card per pocket: name,
// art and a count. Supplies, Treasures, Ascension, Relics, Crafting and Other open inside the Bag
// (#/bag/<pocket>), with a back control in the header; Altar, Incubators, Favorites, Outfits and Skins open the
// screen that already does that job. Inside a pocket it is one category tab at a time (All first) of uniform item
// tiles; a tile opens a detail panel beside the grid (a sheet on phones, never a blocking
// modal) with what the item is, what it is for, where it comes from, what it
// sells for and the actions it has. The Merchant is a category rail and a buy
// card per good, each with a quantity stepper, the total and one Buy button; a
// Sell rail sells bag items back the same way. Nothing is ever bought for the
// player.
import { CONFIG } from '../shared/config.ts';
import { SPECIES, BOSSES } from '../shared/species.ts';
import { ELEMENTS } from '../shared/elements.ts';
import { GROUND, REGIONS, keyHuntLevels } from '../shared/grounds.ts';
import { ITEM, ITEMS, REVIVE_HP, RELIC_STATS, relicName, relicDesc, relicSidesText, relicGrade, relicScrapCoins, relicQualityAtMost, bulkScrapList, shopPriceAt, PRICE_IN_KILLS, type Relic, type RelicStat, type BulkScrap, type ItemDef } from '../shared/items.ts';
import { RECIPES, RECIPE, craftable, craftedInPeriod, periodEnds, type Recipe } from '../shared/recipes.ts';
import { LADDER_BASE_LEVEL } from '../shared/ladder.ts';
import type { ClientMsg, CreatureDTO } from '../shared/protocol.ts';
import { ALTAR, altarLeft } from '../shared/altar.ts';
import { EGG_UNLOCK_LEVEL, incubatorSlots } from '../shared/eggs.ts';
import { OUTFITS, ownsOutfit } from '../shared/outfits.ts';
import { SKINS, ownsSkin } from '../shared/skins.ts';
import { S, serverNow } from './state.ts';
import { iconUrl, uiIcon, spriteUrl, artInfo } from './assets.ts';
import { ICON } from './icons.ts';
import { route, go } from './router.ts';
import { altar } from './creatures.ts';
import { esc, full, num, cname } from './fmt.ts';
import { sealChance, pct } from './hud.ts';
import { openDialog, toast } from './dialog.ts';
import type { Grade } from '../shared/grade.ts';
import { charmItemBlocked, catchCharmEffect, catchCharmUntil } from '../shared/catchcharm.ts';
import { charmUntil } from '../shared/auracharm.ts';
import { forgeHtml, heldBagHtml, heldRange, forgeButton, forgeElSelect } from './held.ts';
import { HELD, HELD_ITEMS, PIECES_PER_HELD, QUALITY_MIN, QUALITY_MAX, heldPiece, qualityAtLeast, pieceChance, pieceOf } from '../shared/held.ts';
import { TRAIL, type TrailTrainer } from '../shared/trail.ts';
import { RANKED } from '../shared/ranked.ts';
import { relicGradeBadge } from './badges.ts';

let send: (m: ClientMsg) => void = () => {};
let rerender: () => void = () => {};
export function bindBagShop(o: { send: (m: ClientMsg) => void; render: () => void }) {
  send = o.send; rerender = o.render;
  // Typing in a quantity box updates its total as you type; Escape closes the item sheet.
  document.addEventListener('input', (e) => {
    const el = e.target as HTMLInputElement;
    if (el.dataset?.bsQty === undefined) return;
    setQty(el.dataset.bsQty, Number(el.value), false);
    rerender();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && bagOpen && S.page === 'bag') { bagOpen = false; rerender(); }
  });
}

const coin = (n: number) => `<span class="coinv"><img src="${iconUrl('coin')}" alt="coins">${full(n)}</span>`;
const regionName = (r?: string) => REGIONS.find((g) => g.id === r)?.name ?? '';
const bossOf = (r?: string) => BOSSES.find((b) => b.region === r);
const phone = () => typeof matchMedia === 'function' && matchMedia('(max-width: 760px)').matches;
/** The server sells at most this many in one purchase. */
const MAX_BUY = 999;

/** The Merchant's price for one at your level (potions and Revives scale with it). */
const priceOf = (id: string) => shopPriceAt(id, S.trainer?.level ?? 1) ?? 0;

// ---------------- quantity steppers ----------------
// One quantity per card ("buy:rune-seal", "sell:pelt"), kept while the page re-renders.
const qty = new Map<string, number>();
function maxFor(key: string): number {
  const t = S.trainer!;
  const [mode, id] = key.split(':');
  if (mode === 'craft') return Math.max(1, RECIPE[id] ? craftable(RECIPE[id], t, serverNow()).n : 1);
  const it = ITEM[id];
  if (!it) return 1;
  if (mode === 'buy') return Math.max(1, Math.min(MAX_BUY, Math.floor(t.coins / (priceOf(id) || 1))));
  return Math.max(1, t.bag[id] ?? 0);
}
function getQty(key: string) { return Math.min(qty.get(key) ?? 1, maxFor(key)); }
function setQty(key: string, n: number, clamp = true) {
  const v = Math.floor(n) || 1;
  qty.set(key, clamp ? Math.max(1, Math.min(maxFor(key), v)) : Math.max(1, Math.min(MAX_BUY * 1000, v)));
}

function stepper(key: string, label: string) {
  const q = getQty(key);
  return `<div class="bs-step" role="group" aria-label="${esc(label)}">
    <button class="bs-sb" data-act="bs-dec" data-id="${key}" aria-label="One fewer"${q <= 1 ? ' disabled' : ''}>−</button>
    <input data-bs-qty="${key}" value="${q}" inputmode="numeric" aria-label="Quantity">
    <button class="bs-sb" data-act="bs-inc" data-id="${key}" aria-label="One more"${q >= maxFor(key) ? ' disabled' : ''}>+</button>
    <button class="bs-sb bs-max" data-act="bs-max" data-id="${key}">Max</button></div>`;
}

/** Strength as filled dots out of four (catch ×1 to ×4, heal 20% steps). */
const dots = (n: number, of = 4) => `<span class="bs-dots" aria-hidden="true">${Array.from({ length: of }, (_, i) => `<i class="${i < n ? 'on' : ''}"></i>`).join('')}</span>`;

// ---------------- the Merchant ----------------
type Rail = 'seals' | 'potions' | 'sell';
let rail: Rail = 'seals';
const sellable = () => ITEMS.filter((i) => i.sellPrice > 0 && (S.trainer!.bag[i.id] ?? 0) > 0);

function buyCard(i: ItemDef) {
  const t = S.trainer!;
  const key = `buy:${i.id}`, q = getQty(key), price = priceOf(i.id), total = price * q;
  const have = t.bag[i.id] ?? 0;
  const g = GROUND[t.ground], sp = g ? SPECIES[g.species] : null;
  const first = t.settings.seals.find((r) => r.on)?.id === i.id;
  const stat = i.kind === 'seal'
    ? `<div class="bs-stat">Catch ×${i.catchMult} ${dots(i.catchMult ?? 1)}</div>${sp ? `<div class="bs-quiet" title="Chance one throw catches the wild ${esc(sp.name)}, with your team's catch bonuses and any Catch Charm running">${pct(sealChance(i.id))} per throw on ${esc(sp.name)}</div>` : ''}`
    : i.kind === 'revive'
      ? `<div class="bs-stat" title="Used by itself when your whole team faints on a hunt">Revives the team at ${Math.round(REVIVE_HP * 100)}% HP</div>`
      : `<div class="bs-stat">Heals ${Math.round((i.heal ?? 0) * 100)}% HP ${dots(Math.round((i.heal ?? 0) * 5))}</div>`;
  return `<article class="bs-card${first ? ' first' : ''}" data-key="${key}">
    ${first ? '<span class="bs-tag" title="Your Automation throws this seal first">Thrown first</span>' : ''}
    <img class="bs-art" src="${iconUrl(i.id, 64)}" alt="">
    <h5>${esc(i.name)}</h5>${stat}
    <div class="bs-have">You have <b>${full(have)}</b></div>
    <div class="bs-price">${coin(price)} <span class="muted">each</span></div>
    ${stepper(key, `How many ${i.name}`)}
    <div class="bs-total"><span class="muted">Total</span> ${coin(total)}</div>
    <button class="btn primary bs-go" data-act="bs-buy" data-id="${i.id}"${t.coins < total ? ' disabled title="Not enough coins"' : ''}>Buy</button></article>`;
}

function sellCard(i: ItemDef) {
  const t = S.trainer!;
  const key = `sell:${i.id}`, q = getQty(key), have = t.bag[i.id] ?? 0;
  return `<article class="bs-card" data-key="${key}">
    <img class="bs-art" src="${iconUrl(i.id, 64)}" alt="">
    <h5>${esc(i.name)}</h5><div class="bs-quiet">${esc(KIND_LABEL[i.kind] ?? 'Item')}</div>
    <div class="bs-have">You have <b>${full(have)}</b></div>
    <div class="bs-price">${coin(i.sellPrice)} <span class="muted">each</span></div>
    ${stepper(key, `How many ${i.name} to sell`)}
    <div class="bs-total"><span class="muted">You get</span> ${coin(i.sellPrice * q)}</div>
    <button class="btn bs-go" data-act="bs-sell" data-id="${i.id}">Sell</button></article>`;
}

export function merchantPane() {
  const sells = sellable();
  if (rail === 'sell' && !sells.length) rail = 'seals';
  const rails: [Rail, string, string, number][] = [
    ['seals', 'Seals', iconUrl('rune-seal'), 0],
    ['potions', 'Potions', iconUrl('mead'), 0],
    ['sell', 'Sell', iconUrl('coin'), sells.length],
  ];
  const nav = `<nav class="bs-rail" aria-label="Merchant">${rails.map(([id, name, ic, n]) => `<button class="${rail === id ? 'on' : ''}" data-act="bs-rail" data-id="${id}" aria-pressed="${rail === id}">${uiIcon('ui-rail-' + id, `<img src="${ic}" alt="">`, '', 32)}${name}${n ? `<span class="bs-n">${full(n)}</span>` : ''}</button>`).join('')}</nav>`;
  const cards = rail === 'sell' ? sells.map(sellCard)
    : ITEMS.filter((i) => i.shopPrice && (rail === 'seals' ? i.kind === 'seal' : i.kind === 'potion' || i.kind === 'revive')).map(buyCard);
  return `<div class="bs-shop">${nav}<div class="bs-cards" data-fit="bs-${rail}">${cards.join('') || '<p class="hint">Nothing to sell.</p>'}</div></div>`;
}

// ---------------- the Bag ----------------
const KIND_LABEL: Record<string, string> = {
  seal: 'Seal', potion: 'Potion', revive: 'Revive', ascension: 'Ascension item', star: 'Star item', token: 'Boss key',
  shard: 'Shrine material', charm: 'Charm', fragment: 'Boss Ladder piece', crest: 'Trophy', tablet: 'Rune Tablet', tag: 'Name Tag', awakening: 'Awakening', boost: 'Timed charm', piece: 'Held-item piece',
};
/** The pockets that hold bag items. */
type ItemPocket = 'supplies' | 'treasures' | 'ascension' | 'other';
/**
 * Bag categories in order, each with the pocket it sits in; a category without one lands in the Other pocket.
 * "Stones and stars" was once labelled "Growing", then "Ascension and stars".
 */
const CATS: { id: string; name: string; pocket?: ItemPocket; test: (i: ItemDef) => boolean }[] = [
  { id: 'seals', name: 'Seals', pocket: 'supplies', test: (i) => i.kind === 'seal' },
  { id: 'potions', name: 'Potions', pocket: 'supplies', test: (i) => i.kind === 'potion' || i.kind === 'revive' },
  { id: 'boosts', name: 'Timed charms', pocket: 'supplies', test: (i) => i.kind === 'boost' },
  { id: 'tags', name: 'Name tags', pocket: 'supplies', test: (i) => i.kind === 'tag' },
  { id: 'keys', name: 'Boss keys', pocket: 'treasures', test: (i) => i.kind === 'token' },
  { id: 'ladder', name: 'Boss Ladder', pocket: 'treasures', test: (i) => i.kind === 'fragment' || i.kind === 'crest' || i.kind === 'tablet' },
  { id: 'shrine', name: 'Shrine shards', pocket: 'treasures', test: (i) => i.kind === 'shard' },
  { id: 'held', name: 'Held items', pocket: 'treasures', test: (i) => i.kind === 'piece' },
  { id: 'charms', name: 'Charms', pocket: 'treasures', test: (i) => i.kind === 'charm' },
  { id: 'ascension', name: 'Stones and stars', pocket: 'ascension', test: (i) => i.kind === 'ascension' || i.kind === 'star' },
  { id: 'awakening', name: 'Awakening', pocket: 'ascension', test: (i) => i.kind === 'awakening' },
];
// Any kind no category above names (a new item kind) still shows, under Other, with its own description.
CATS.push({ id: 'other', name: 'Other', test: (i) => !CATS.some((c) => c.id !== 'other' && c.test(i)) });
const pocketOf = (c: { pocket?: ItemPocket }): ItemPocket => c.pocket ?? 'other';
/** The category tab open inside a pocket ('' is All). */
let bagCat = '';
/** The pocket the tabs above belong to; opening another starts on All. */
let bagPocket = '';
/** The item in the detail panel: "i:<item id>" or "r:<relic id>". */
let bagSel = '';
/** On phones the detail is a sheet that opens on a tap and closes again. */
let bagOpen = false;
let relicFilter = '';

type RelicRow = { r: Relic; on?: CreatureDTO };
function relicRows(): RelicRow[] {
  const t = S.trainer!;
  const equipped = [...S.creatures.values()].filter((c) => c.relic).map((c) => ({ r: c.relic!, on: c }));
  return [...t.relics.map((r) => ({ r })), ...equipped].sort((a, b) => b.r.quality - a.r.quality);
}

/** What an item is for, in a line. */
function useLine(i: ItemDef): string {
  const rg = regionName(i.region), boss = bossOf(i.region);
  switch (i.kind) {
    case 'seal': return 'Thrown at wild creatures on the hunt, in the order set under Automation.';
    case 'potion': return 'Drunk on the hunt when the lead falls below the HP set under Automation.';
    case 'revive': return `Used by itself when your whole team faints on a hunt: every fighter gets up at ${Math.round(REVIVE_HP * 100)}% HP, with no rest and no XP lost.`;
    case 'ascension': return i.element ? `Ascends ${ELEMENTS[i.element].name} creatures at their ascension level, and trains ${ELEMENTS[i.element].name} creatures' stats for hunts (Stone training), on the creature's page.` : `Ascends a ${rg} creature once it reaches its ascension level, on the creature's page.`;
    case 'star': return `Earns stars for final-stage creatures and refines ${rg} creatures, on the creature's page.`;
    case 'token': return `One per fight against ${boss?.name ?? 'the boss'} and its Boss Ladder steps, ${S.trailOn ? 'on the Trail' : 'on the Bosses page'}.`;
    case 'shard': return 'Ten build one shrine of a random tier, on the Team page.';
    case 'fragment': return 'Ten make one crest or Rune Tablet at the crafting bench.';
    case 'crest': { const title = RECIPES.find((r) => r.out.item === i.id)?.title; return `A trophy that stays on your account. Crafting it unlocks the title ${title ?? 'of its boss'}, chosen under Title on your player card.`; }
    case 'tablet': return "Applied on a creature's page: its Coverage move takes this element.";
    case 'awakening': return i.id.includes('stone') ? "Used on the creature's page. Irreversible." : 'Ten make an Awakening Stone at the crafting bench.';
    case 'charm': return 'Given to one creature on its page.';
    case 'boost': return i.boost?.charm === 'aura'
      ? `Used from the Bag: ${i.boost.hours} h of Aura odds ×${CONFIG.auraCharm.oddsMult} on your hunt catches, after any Aura Charm running. Not while a Catch Charm runs.`
      : `Used from the Bag: ${i.boost?.hours ?? 1} h of ${catchCharmEffect()} per throw, added to any Catch Charm running. Not while an Aura Charm runs.`;
    case 'tag': return "Names one creature, on the creature's page.";
    case 'piece': return `Ten forge the ${HELD[pieceOf(i.id) ?? '']?.name ?? 'item'} at the crafting bench.`;
  }
  return '';
}

/** Where an item comes from, in a line. On the bench itself (onBench) the bench is not repeated. */
function fromLine(i: ItemDef, onBench = false): string {
  const rg = regionName(i.region), boss = bossOf(i.region);
  const recipes = RECIPES.filter((r) => r.out.item === i.id), benchLv = Math.min(...recipes.map((r) => r.minLevel ?? 0));
  const made = !onBench && recipes.length ? ` Also made at the crafting bench${benchLv ? ` from Lv ${full(benchLv)}` : ''}.` : '';
  switch (i.kind) {
    case 'seal': case 'potion': case 'revive': return `The Merchant, ${full(priceOf(i.id))} coins each${PRICE_IN_KILLS[i.id] ? ' at your level' : ''}.${made}`;
    case 'ascension': return i.legacy ? 'Retired: element stones took its place. It still ascends and trades.' : `Drops from ${ELEMENTS[i.element!].name} wilds on any hunt, and from the Altar.${made}`;
    case 'star': return `A rare drop on the later ${rg} hunts, 1 in ${full(CONFIG.starMaterialOneIn)} kills; one or two from every win against ${boss?.name ?? 'its boss'}, and the Boss Ladder.${made}`;
    // Keys drop on the region's hunts from tokenFrom up to tokenTo (grounds.ts bossToken, server/hunt.ts).
    case 'token': { const k = i.region ? keyHuntLevels(i.region) : null; return `${rg} hunts of Lv ${k ? `${full(k.lo)}-${full(k.hi)}` : '?'}, 1 in ${full(CONFIG.bossTokenOneIn)} kills.${made}`; }
    case 'shard': return `A very rare drop from wild creatures of Lv ${full(CONFIG.shrineShardMinLevel)} and up.`;
    case 'fragment': return 'Won on the Boss Ladder.';
    case 'crest': case 'tablet': return 'Made at the crafting bench.';
    case 'awakening': return i.id.includes('stone') ? 'Made at the crafting bench from ten shards.' : 'Won against bosses and on the Boss Ladder.';
    case 'charm': case 'tag': return 'The Diamond shop.';
    case 'boost': return i.boost?.hours === 24 ? `Ranked seasons: the ${i.boost.charm === 'aura' ? 'Gold' : 'Platinum'} milestone paid it until October 2026 (1 h charms now). Also on the market.` : `Ranked seasons: every ${RANKED.charm.winsPer} wins that gave trophies (at most ${RANKED.charm.weeklyHours} h a week) and the ${i.boost?.charm === 'aura' ? 'Gold' : 'Platinum'} milestone. Also on the market.`;
    case 'piece': {
      const pays = TRAIL.flatMap((p) => p.nodes).filter((n): n is TrailTrainer => n.kind === 'trainer' && n.reward.items.some((x) => x.item === i.id)).map((n) => n.name);
      const b = Math.round(pieceChance('boss') * 100), l = Math.round(pieceChance('ladder') * 100), who = boss?.name ?? 'its boss';
      const odds = b === l ? `${b}% of wins against ${who} and its Boss Ladder steps` : `${b}% of wins against ${who}, ${l}% of its Boss Ladder steps`;
      return `${odds}${S.trailOn ? ', once you have cleared it on the Trail' : ''}.${S.trailOn && pays.length ? ` Also the first wins against ${pays.join(' and ')} on the Trail.` : ''}`;
    }
  }
  return '';
}

/** A bag tile: art, name and a big count (a relic shows its quality instead). */
function tile(key: string, img: string, name: string, count: string, sel: boolean, extra = '') {
  return `<button class="bg-tile bt-tile${sel ? ' on' : ''}${extra}" data-act="bag-pick" data-id="${key}" data-key="${key}" aria-pressed="${sel}" title="${esc(name)}">
    <span class="bg-art"><img src="${img}" alt=""></span><span class="bg-nm">${esc(name)}</span><span class="bt-cnt">${count}</span></button>`;
}

const fact = (k: string, v: string) => `<div><dt>${k}</dt><dd>${v}</dd></div>`;

function itemDetail(i: ItemDef) {
  const t = S.trainer!;
  const n = t.bag[i.id] ?? 0, dev = t.devItems?.[i.id] ?? 0, own = Math.max(0, n - dev);
  const uses = RECIPES.filter((r) => r.in.some((x) => x.item === i.id));
  // Many recipes from one input (Tablet Fragments make any element's tablet) read as one line of outputs.
  const sameIn = uses.length > 3 && uses.every((r) => r.in.find((y) => y.item === i.id)!.qty === uses[0].in.find((y) => y.item === i.id)!.qty);
  const recLine = (r: Recipe) => { const x = r.in.find((y) => y.item === i.id)!; return `<li><button class="cr-link" data-act="bag-recipe" data-id="${r.id}"><span>${full(x.qty)} →</span><img src="${iconUrl(r.out.item)}" alt=""><span>${full(r.out.qty)} ${esc(ITEM[r.out.item].name)}</span></button></li>`; };
  const makes = !uses.length ? '' : `<div class="bd-sec"><h6>Crafts into</h6><ul class="bd-rec">${sameIn
    ? `<li><span>${full(uses[0].in.find((y) => y.item === i.id)!.qty)} → one of</span><span class="bd-outs">${uses.map((r) => `<button class="cr-link" data-act="bag-recipe" data-id="${r.id}" title="${esc(ITEM[r.out.item].name)}"><img src="${iconUrl(r.out.item)}" alt="${esc(ITEM[r.out.item].name)}"></button>`).join('')}</span></li>`
    : uses.slice(0, 4).map(recLine).join('') + (uses.length > 4 ? `<li class="muted">and ${uses.length - 4} more in Crafting</li>` : '')}</ul></div>`;
  const sKey = `sell:${i.id}`, q = getQty(sKey);
  const first = i.kind === 'seal' && t.settings.seals.find((r) => r.on)?.id === i.id;
  const acts: string[] = [];
  if (i.sellPrice > 0) acts.push(`<div class="bd-sell">${stepper(sKey, `How many ${i.name} to sell`)}<button class="btn small" data-act="bs-sell" data-id="${i.id}">Sell · ${full(i.sellPrice * q)} ${i.sellPrice * q === 1 ? 'coin' : 'coins'}</button></div>`);
  if (i.tradable && own > 0) acts.push(`<button class="btn small" data-act="list-item" data-id="${i.id}">List on Market…</button>`);
  if (i.boost) {
    const now = serverNow(), why = charmItemBlocked(t.boosts, i.boost.charm, now);
    const until = i.boost.charm === 'aura' ? charmUntil(t.boosts, now) : catchCharmUntil(t.boosts, now);
    const label = until ? `Use · +${i.boost.hours} h` : `Use · ${i.boost.hours} h`;
    acts.push(`<button class="btn small primary" data-act="use-charm" data-id="${i.id}"${why ? ` disabled title="${esc(why)}"` : ''}>${label}</button>`);
  }
  if (i.kind === 'seal' && !first) acts.push(`<button class="btn small" data-act="seal" data-id="${i.id}">Throw first</button>`);
  if (i.shopPrice) acts.push(`<a class="btn small" href="#/shop" data-act="bs-more" data-id="${i.kind === 'seal' ? 'seals' : 'potions'}">Buy more</a>`);
  if (i.kind === 'token') acts.push('<a class="btn small" href="#/bosses">Bosses</a>');
  if (i.kind === 'shard') acts.push('<a class="btn small" href="#/creatures/team/shrines">Shrines</a>');
  if (['ascension', 'star', 'tablet', 'charm', 'tag'].includes(i.kind) || (i.kind === 'awakening' && i.id.includes('stone'))) acts.push('<a class="btn small" href="#/creatures">Creatures</a>');
  return `<div class="bd-head"><img class="bd-art" src="${iconUrl(i.id)}" alt=""><div><h4>${esc(i.name)}</h4>
      <div class="bd-kind">${esc(KIND_LABEL[i.kind] ?? 'Item')}${i.region ? ` · ${esc(regionName(i.region))}` : ''}</div></div></div>
    <p class="bd-desc">${esc(i.desc)}</p>
    <dl class="bd-facts">
      ${fact('You have', `${full(n)}${dev ? ` <span class="muted">(${full(dev)} admin)</span>` : ''}`)}
      ${fact('Sells for', i.sellPrice ? `${coin(i.sellPrice)} each` : '<span class="muted">No merchant buys it</span>')}
      ${fact('Market', i.tradable ? 'Tradeable' : 'Account-bound')}
      ${i.kind === 'seal' && GROUND[t.ground] ? fact('Per throw', `${pct(sealChance(i.id))} on ${esc(SPECIES[GROUND[t.ground].species].name)}`) : ''}
    </dl>
    ${useLine(i) ? `<div class="bd-sec"><h6>Used for</h6><p>${esc(useLine(i))}</p></div>` : ''}
    ${fromLine(i) ? `<div class="bd-sec"><h6>Comes from</h6><p>${esc(fromLine(i))}</p></div>` : ''}
    ${makes}${i.kind === 'piece' && pieceOf(i.id) ? `<div class="bd-sec"><h6>Forges into</h6><ul class="bd-rec"><li><span>${full(PIECES_PER_HELD)} →</span><span class="bd-outs"><button class="cr-link" data-act="bag-forge" data-id="${pieceOf(i.id)}" title="${esc(HELD[pieceOf(i.id)!].name)}"><img src="${iconUrl(pieceOf(i.id)!)}" alt="${esc(HELD[pieceOf(i.id)!].name)}"></button><span>${esc(HELD[pieceOf(i.id)!].name)}</span></span></li></ul></div>` : ''}
    ${acts.length ? `<div class="bd-acts">${acts.join('')}</div>` : ''}`;
}

function relicDetail(x: RelicRow) {
  const { r, on } = x;
  const scrap = relicScrapCoins(r);
  const sides = relicSidesText(r);
  return `<div class="bd-head"><img class="bd-art" src="${iconUrl(`relic-${r.stat}`)}" alt=""><div><h4>${relicGradeBadge(r, 'sm')}${esc(relicName(r))}</h4>
      <div class="bd-kind">Relic · Quality ${r.quality}${r.dev ? ' · admin' : ''}</div></div></div>
    <p class="bd-desc">${esc(relicDesc(r))}, ${r.stat === 'element' || r.stat === 'damage' ? 'for the creature that wears it' : 'for the whole team; only the team\'s best of this kind counts'}.</p>
    <div class="bd-q" role="meter" aria-valuenow="${r.quality}" aria-valuemin="1" aria-valuemax="100" aria-label="Quality"><i style="width:${r.quality}%"></i></div>
    <dl class="bd-facts">
      ${fact('Worn by', on ? `<a class="link" href="#/creature/${on.id}">${esc(cname(on))}</a>` : '<span class="muted">Nobody</span>')}
      ${fact('Scraps for', coin(scrap))}
      ${r.quality >= 60 ? fact('Rolls this high', `<span title="Hunt drops of Q${r.quality} or better">1 in ${full(Math.round(1 / (1 - relicQualityAtMost('wild', r.quality - 1))))} drops</span>`) : ''}
      ${sides ? fact(r.sides!.length > 1 ? 'Sides on hunts' : 'Side on hunts', `<span title="For the whole team; only the team's best side of each kind counts.">${esc(sides)}</span>`) : ''}
      ${fact('Market', r.dev ? 'Account-bound' : 'Tradeable')}
    </dl>
    <div class="bd-sec"><h6>Used for</h6><p>One relic per creature, put on from the creature's page.</p></div>
    <div class="bd-sec"><h6>Comes from</h6><p>A rare drop from wild creatures of Lv 15 and up, and boss wins.</p></div>
    ${on ? '' : `<div class="bd-acts">${r.dev ? '' : `<button class="btn small" data-act="list-relic" data-id="${r.id}">List on Market…</button>`}<button class="btn small" data-act="bs-scrap" data-id="${r.id}">Scrap · ${full(scrap)} coins</button></div>`}`;
}

// ---------------- pockets ----------------
/** What the Bag holds right now, worked out once per render. */
interface BagState {
  cats: ((typeof CATS)[number] & { items: ItemDef[] })[];
  /** Finished held items (client/held.ts), free and worn, and how many there are. */
  heldList: string; heldN: number;
  relics: RelicRow[]; recipes: Recipe[]; forge: string;
}
function bagState(): BagState {
  const t = S.trainer!;
  return {
    cats: CATS.map((c) => ({ ...c, items: ITEMS.filter((i) => c.test(i) && (t.bag[i.id] ?? 0) > 0) })),
    heldList: heldBagHtml(), heldN: (t.held?.length ?? 0) + [...S.creatures.values()].filter((c) => c.held).length,
    relics: relicRows(), recipes: benchRecipes(), forge: forgeHtml(),
  };
}

/** A pocket card on the Bag's front page. */
interface Pocket {
  id: string; name: string;
  /** What is inside, for the card's tooltip. */
  title?: string;
  /** Its picture: an item icon, a UI icon or the first frame of a Kinhall prop. */
  art: string;
  /** The count pill: a count, something ready, nothing inside, or the level it opens at. */
  pill: string; tone: 'n' | 'good' | 'empty' | 'lock';
  /** Opens inside the Bag, at #/bag/<id>. */
  inBag?: boolean;
  /** Or opens another screen (href) or runs an action (act). */
  href?: string; act?: string;
  /** Nothing to open: shown, not clickable. */
  off?: boolean;
}
type Fill = Pick<Pocket, 'pill' | 'tone' | 'off'>;

const plural = (n: number, one: string) => `${full(n)} ${n === 1 ? one : `${one}s`}`;
const pic = (src: string) => `<img src="${src}" alt="">`;
const pill = (p: Pocket) => `<span class="bp-pill ${p.tone}">${p.tone === 'lock' ? ICON.lock : ''}${esc(p.pill)}</span>`;
const EMPTY: Fill = { pill: 'Empty', tone: 'empty', off: true };
const BACK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 5-7 7 7 7"/></svg>';

/** The first frame of a Kinhall prop (art/rig3d/props), at its own size. */
function prop(id: string, fallback: string): string {
  const a = artInfo(id);
  return a ? `<span class="bp-prop" style="--w:${a.w};--h:${a.h};--f:${a.frames};background-image:url('${spriteUrl(id)}')"></span>` : fallback;
}

/** The pockets that hold bag items, by category (CATS); Other only shows while something lands in it. */
const ITEM_POCKETS: { id: ItemPocket; name: string; title: string; icon: string }[] = [
  { id: 'supplies', name: 'Supplies', title: 'Seals, potions, revives, timed charms, name tags', icon: 'rune-seal' },
  { id: 'treasures', name: 'Treasures', title: 'Boss keys, ladder pieces, shrine shards, held items, charms', icon: 'fenrir-token' },
  { id: 'ascension', name: 'Ascension', title: 'Element stones, star items, awakening', icon: 'rune-of-ascent' },
];

/** Every pocket in order: what lives in the Bag first, then the screens that keep the rest. */
function pocketList(st: BagState): Pocket[] {
  const t = S.trainer!, now = serverNow();
  const qty = (id: ItemPocket) => st.cats.filter((c) => pocketOf(c) === id).reduce((a, c) => a + c.items.reduce((b, i) => b + (t.bag[i.id] ?? 0), 0), 0);
  const items = (n: number): Fill => (n ? { pill: plural(n, 'item'), tone: 'n' } : EMPTY);
  const list: Pocket[] = ITEM_POCKETS.map((p) => ({ id: p.id, name: p.name, title: p.title, art: pic(iconUrl(p.icon, 64)), inBag: true, ...items(qty(p.id) + (p.id === 'treasures' ? st.heldN : 0)) }));

  list.push({ id: 'relics', name: 'Relics', art: pic(iconUrl(`relic-${st.relics[0]?.r.stat ?? 'damage'}`)), inBag: true,
    ...(st.relics.length ? { pill: plural(st.relics.length, 'relic'), tone: 'n' } : EMPTY) });

  const ready = st.recipes.filter((r) => craftable(r, t, now).n).length;
  list.push({ id: 'craft', name: 'Crafting', art: prop('prop-hall-bench-midgard', pic(iconUrl('tablet-fragment', 64))), inBag: true,
    ...(ready ? { pill: `${full(ready)} ready`, tone: 'good' } : st.recipes.length ? { pill: plural(st.recipes.length, 'recipe'), tone: 'n' } : st.forge ? { pill: 'Forge', tone: 'n' } : EMPTY) });

  const left = altarLeft(t, now);
  list.push({ id: 'altar', name: 'Altar', title: 'Offer creatures for a stone or star item', art: prop('prop-hall-altar', ''), act: 'bag-altar',
    ...(t.level < ALTAR.minLevel ? { pill: `Lv ${full(ALTAR.minLevel)}`, tone: 'lock', off: true } : { pill: `${full(left)}/${full(ALTAR.weekly)} this week`, tone: left ? 'n' : 'empty' }) });

  const eggs = t.eggs?.length ?? 0;
  list.push({ id: 'eggs', name: 'Incubators', art: prop('prop-hall-hatchery', ''), href: '#/shop/eggs',
    ...(t.level < EGG_UNLOCK_LEVEL ? { pill: `Lv ${full(EGG_UNLOCK_LEVEL)}`, tone: 'lock' } : { pill: `${full(eggs)}/${full(incubatorSlots(t))} hatching`, tone: eggs ? 'n' : 'empty' }) });

  const fav = t.collection?.length ?? 0;
  list.push({ id: 'favorites', name: 'Favorites', art: prop('prop-hall-vault', ''), href: '#/creatures/favorites',
    ...(fav ? { pill: plural(fav, 'creature'), tone: 'n' } : { pill: 'Empty', tone: 'empty' }) });

  list.push({ id: 'outfits', name: 'Outfits', art: uiIcon('ui-outfits', '', 'bp-ui', 64), href: '#/trainer/outfits',
    pill: plural(OUTFITS.filter((o) => ownsOutfit(t, o.id)).length, 'outfit'), tone: 'n' });

  // Skins live in the Diamond shop; the pocket shows while a season sells them or you own one.
  const onSale = new Set(t.skinSeasons ?? []);
  if (SKINS.some((s) => onSale.has(s.season) || ownsSkin(t, s.id))) {
    const own = SKINS.filter((s) => ownsSkin(t, s.id)).length;
    list.push({ id: 'skins', name: 'Skins', art: uiIcon('ui-showcase', '', 'bp-ui', 64), href: '#/shop/diamonds/skins',
      ...(own ? { pill: plural(own, 'skin'), tone: 'n' } : { pill: 'Empty', tone: 'empty' }) });
  }

  const other = qty('other');
  const first = st.cats.find((c) => pocketOf(c) === 'other' && c.items.length)?.items[0];
  if (other && first) list.push({ id: 'other', name: 'Other', art: pic(iconUrl(first.id, 64)), inBag: true, pill: plural(other, 'item'), tone: 'n' });
  return list;
}

function pocketCard(p: Pocket): string {
  const inner = `<span class="bp-art">${p.art}</span><span class="bp-txt"><b class="bp-nm">${esc(p.name)}</b>${pill(p)}</span>`;
  const cls = `bp-card pk-${p.id}${p.off ? ' off' : ''}`, key = `data-key="pk-${p.id}"${p.title ? ` title="${esc(p.title)}"` : ''}`;
  if (p.off) return `<div class="${cls}" ${key} aria-disabled="true">${inner}</div>`;
  if (p.act) return `<button class="${cls}" ${key} data-act="${p.act}">${inner}</button>`;
  return `<a class="${cls}" ${key} href="${p.inBag ? `#/bag/${p.id}` : p.href}">${inner}</a>`;
}

/** The front page: the header card and every pocket. */
function frontPage(list: Pocket[], st: BagState): string {
  const t = S.trainer!;
  const n = st.cats.reduce((a, c) => a + c.items.reduce((b, i) => b + (t.bag[i.id] ?? 0), 0), 0);
  const sub = [plural(n, 'item'), st.relics.length ? plural(st.relics.length, 'relic') : ''].filter(Boolean).join(' · ');
  return `<header class="panel bp-head" data-key="bp-head"><span class="bp-art">${uiIcon('ui-bag', ICON.bag, 'bp-ui', 48)}</span>
    <div class="bp-ht"><h1>Bag</h1><span class="bp-sub">${sub}</span></div><a class="btn small bp-shop" href="#/shop"><img src="${iconUrl('coin')}" alt="">Merchant</a></header>
    <div class="bp-grid" data-key="bp-grid">${list.map(pocketCard).join('')}</div>`;
}

/** An open pocket's header card: back to the pockets, its picture, name and count. */
function pocketHead(p: Pocket): string {
  return `<header class="panel bp-head in pk-${p.id}" data-key="bp-head"><a class="bp-back" href="#/bag" aria-label="Back" title="Bag">${BACK}</a>
    <span class="bp-art">${p.art}</span><div class="bp-ht"><span class="bp-eye">Bag</span><h1>${esc(p.name)}</h1></div>${pill(p)}</header>`;
}

export function bagPane() {
  const t = S.trainer!;
  const st = bagState();
  const list = pocketList(st);
  const p = list.find((x) => x.inBag && x.id === route().sub);
  if (!p) { bagOpen = false; return frontPage(list, st); }
  if (bagPocket !== p.id) { bagPocket = p.id; bagCat = ''; }
  const keep = (ok: boolean, first: string) => { if (!ok) { bagSel = first; if (phone()) bagOpen = false; } };

  let tabs = '', body = '';
  if (p.id === 'craft') {
    const forgeOk = !!st.forge && bagSel.startsWith('h:') && !!HELD[bagSel.slice(2)];
    keep((bagSel.startsWith('c:') && st.recipes.some((r) => r.id === bagSel.slice(2))) || forgeOk, st.recipes[0] ? `c:${st.recipes[0].id}` : st.forge ? `h:${HELD_ITEMS[0].id}` : '');
    body = bench(st.recipes, forgeHtml(bagSel));
  } else if (p.id === 'relics') {
    const relics = st.relics;
    keep(bagSel.startsWith('r:') && relics.some((x) => x.r.id === bagSel.slice(2)), relics[0] ? `r:${relics[0].r.id}` : '');
    const stats = [...new Set(relics.map((x) => x.r.stat))];
    if (!stats.includes(relicFilter as Relic['stat'])) relicFilter = '';
    const shown = relics.filter((x) => !relicFilter || x.r.stat === relicFilter);
    const bar = `<div class="bt-bar"><span class="muted">${full(t.relics.length)} in the bag · ${full(relics.length - t.relics.length)} worn</span><span class="spacer"></span>
      ${stats.length > 1 ? `<select data-relic-filter aria-label="Relic type"><option value="">All relics</option>${stats.map((s) => `<option value="${s}"${relicFilter === s ? ' selected' : ''}>${esc(RELIC_STATS.find((d) => d.stat === s)!.name)}s</option>`).join('')}</select>` : ''}
      ${t.relics.length ? '<button class="btn small" data-act="bs-bulk-scrap">Scrap relics…</button>' : ''}</div>`;
    const tiles = shown.map(({ r, on }) => tile(`r:${r.id}`, iconUrl(`relic-${r.stat}`, 48), relicName(r), `${relicGradeBadge(r, 'sm')}Q${r.quality}`, bagSel === `r:${r.id}`, on ? ' worn' : ''));
    // Many relics page to fit the screen instead of scrolling (client/fitlist.ts).
    body = relics.length ? `${bar}<div class="bt-grid" data-fit="bag-relics">${tiles.join('')}</div>` : '';
  } else {
    // An item pocket: its categories as tabs (All first) when it holds more than one.
    const cats = st.cats.filter((c) => pocketOf(c) === p.id && (c.items.length || (c.id === 'held' && st.heldList)));
    if (bagCat && !cats.some((c) => c.id === bagCat)) bagCat = '';
    const items = bagCat ? cats.find((c) => c.id === bagCat)!.items : cats.flatMap((c) => c.items);
    keep(bagSel.startsWith('i:') && items.some((i) => i.id === bagSel.slice(2)), items[0] ? `i:${items[0].id}` : '');
    const tabList = [{ id: '', name: 'All', n: cats.reduce((a, c) => a + c.items.length, 0) }, ...cats.map((c) => ({ id: c.id, name: c.name, n: c.items.length }))];
    if (cats.length > 1) tabs = `<nav class="bg-cats bt-tabs" role="tablist" aria-label="${esc(p.name)}">${tabList.map((x) => `<button role="tab" class="${bagCat === x.id ? 'on' : ''}" data-act="bag-cat" data-id="${x.id}" aria-selected="${bagCat === x.id}">${esc(x.name)}${x.n ? `<span class="bt-n">${full(x.n)}</span>` : ''}</button>`).join('')}</nav>`;
    const grid = items.length ? `<div class="bt-grid">${items.map((i) => tile(`i:${i.id}`, iconUrl(i.id, 48), i.name, full(t.bag[i.id]), bagSel === `i:${i.id}`)).join('')}</div>` : '';
    // Finished held items list beside their pieces: on the Held items tab, and under All.
    const heldHere = cats.some((c) => c.id === 'held') && (bagCat === 'held' || bagCat === '') ? st.heldList : '';
    body = bagCat === 'held' ? heldHere + grid : grid + heldHere;
  }

  const relicSel = bagSel.startsWith('r:') ? st.relics.find((x) => x.r.id === bagSel.slice(2)) : undefined;
  const detail = bagSel.startsWith('i:') && ITEM[bagSel.slice(2)] ? itemDetail(ITEM[bagSel.slice(2)])
    : relicSel ? relicDetail(relicSel)
    : bagSel.startsWith('c:') && RECIPE[bagSel.slice(2)] ? recipeDetail(RECIPE[bagSel.slice(2)])
    : bagSel.startsWith('h:') && HELD[bagSel.slice(2)] ? forgeDetail(bagSel.slice(2)) : '';
  const aside = detail ? `<aside class="panel bg-detail${bagOpen ? ' open' : ''}" aria-label="${bagSel.startsWith('c:') || bagSel.startsWith('h:') ? 'Recipe' : 'Item'} details"><button class="bd-close" data-act="bag-close" aria-label="Close">×</button>${detail}</aside>` : '';
  return `${pocketHead(p)}${tabs}<div class="bg-wrap"><div class="bg-main">${body}</div>${aside}</div>`;
}

// ---------------- crafting bench ----------------
/** Recipes for regions you've reached, plus any you hold the inputs for. */
function benchRecipes(): Recipe[] {
  const t = S.trainer!;
  // Boss Ladder crests and Rune Tablets show from the ladder's first step (or once you hold their fragments).
  const ladderOut = (r: Recipe) => ITEM[r.out.item].kind === 'crest' || ITEM[r.out.item].kind === 'tablet';
  // Awakening Stones show once you hold their shards.
  const awakenOut = (r: Recipe) => ITEM[r.out.item].kind === 'awakening';
  return RECIPES.filter((r) => ITEM[r.out.item] && r.in.every((x) => ITEM[x.item])
    && ((!awakenOut(r) && t.level >= (REGIONS.find((g) => g.id === r.region)?.base ?? 0) && (!ladderOut(r) || t.level >= (r.minLevel ?? LADDER_BASE_LEVEL))) || r.in.some((x) => (t.bag[x.item] ?? 0) > 0)));
}

/** Bench groups by what the output is for; an output kind none names lands under Other. */
const BENCH_GROUPS: { id: string; name: string; kinds: string[] }[] = [
  { id: 'keys', name: 'Boss keys', kinds: ['token'] },
  { id: 'stars', name: 'Star items', kinds: ['star'] },
  { id: 'ascension', name: 'Ascension items', kinds: ['ascension'] },
  { id: 'tablets', name: 'Rune Tablets', kinds: ['tablet'] },
  { id: 'awakening', name: 'Awakening Stones', kinds: ['awakening'] },
  { id: 'ladder', name: 'Boss Ladder crests', kinds: ['crest'] },
];

/** One recipe at a glance: the output, then what it takes, each greyed while you hold too few. */
function recipeCard(r: Recipe) {
  const t = S.trainer!;
  const can = craftable(r, t, serverNow());
  const out = ITEM[r.out.item];
  const ins = r.in.map((x) => {
    const have = t.bag[x.item] ?? 0;
    return `<span class="cr-in${have >= x.qty ? '' : ' short'}" title="${esc(ITEM[x.item].name)}: ${full(have)} of ${full(x.qty)}"><img src="${iconUrl(x.item)}" alt="${esc(ITEM[x.item].name)}"><small>${num(have)}/${num(x.qty)}</small></span>`;
  });
  if (r.coins) ins.push(`<span class="cr-in${t.coins >= r.coins ? '' : ' short'}" title="${full(r.coins)} coins"><img src="${iconUrl('coin')}" alt="Coins"><small>${num(r.coins)}</small></span>`);
  const sel = bagSel === `c:${r.id}`;
  const state = can.n ? `<span class="cr-ok">Can craft ${full(can.n)}</span>` : `<span class="cr-why">${esc(can.why)}</span>`;
  return `<button class="bg-tile cr-card${sel ? ' on' : ''}${can.n ? '' : ' no'}" data-act="bag-pick" data-id="c:${r.id}" data-key="c:${r.id}" aria-pressed="${sel}">
    <span class="cr-row"><span class="cr-out"><img src="${iconUrl(out.id)}" alt="">${r.out.qty > 1 ? `<span class="bg-n">×${full(r.out.qty)}</span>` : ''}</span><span class="cr-arrow" aria-hidden="true">←</span><span class="cr-ins">${ins.join('')}</span></span>
    <span class="cr-nm">${esc(out.name)}</span>${state}</button>`;
}

function bench(recipes: Recipe[], forge = '') {
  const groups = BENCH_GROUPS.map((g) => ({ ...g, rs: recipes.filter((r) => g.kinds.includes(ITEM[r.out.item].kind)) }));
  groups.push({ id: 'other', name: 'Other', kinds: [], rs: recipes.filter((r) => !BENCH_GROUPS.some((g) => g.kinds.includes(ITEM[r.out.item].kind))) });
  return `<div class="cr-bench" id="bench">${groups.filter((g) => g.rs.length).map((g) => `<section class="cr-sec"><h4 class="sec">${esc(g.name)}</h4>
    <div class="cr-grid">${g.rs.map(recipeCard).join('')}</div></section>`).join('')}${forge}</div>`;
}

/** A recipe in the detail panel: what it makes and what that does, what it takes and where each input comes from. */
function recipeDetail(r: Recipe) {
  const t = S.trainer!;
  const now = serverNow();
  const can = craftable(r, t, now);
  const o = ITEM[r.out.item];
  const key = `craft:${r.id}`, q = can.n ? getQty(key) : 1;
  const inWorth = r.in.reduce((a, x) => a + x.qty * ITEM[x.item].sellPrice, 0);
  const limit = r.perWeek !== undefined ? `${full(Math.max(0, r.perWeek - craftedInPeriod(t, r, now)))} of ${full(r.perWeek)} left ${(r.everyWeeks ?? 1) > 1 ? `until ${new Date(periodEnds(r, now)).toISOString().slice(0, 10)}` : 'this week'}` : '';
  const row = (img: string, name: string, have: number, need: number, from: string) => `<li class="${have >= need * q ? '' : 'short'}"><img src="${img}" alt=""><div><b>${esc(name)}</b>${from ? `<span>${esc(from)}</span>` : ''}</div><em>${full(have)} / ${full(need * q)}</em></li>`;
  const ins = r.in.map((x) => row(iconUrl(x.item), ITEM[x.item].name, t.bag[x.item] ?? 0, x.qty, fromLine(ITEM[x.item])));
  if (r.coins) ins.push(row(iconUrl('coin'), 'Coins', t.coins, r.coins, ''));
  // Things the bench makes that also drop (boss keys, the Dragon Pearl): say so, so the bench never reads as the only source.
  const found = o.kind === 'token' || o.kind === 'star' ? fromLine(o, true) : '';
  return `<div class="bd-head"><img class="bd-art" src="${iconUrl(o.id)}" alt=""><div><h4>${r.out.qty > 1 ? `${full(r.out.qty)} × ` : ''}${esc(o.name)}</h4>
      <div class="bd-kind">${esc(KIND_LABEL[o.kind] ?? 'Item')}${o.region ? ` · ${esc(regionName(o.region))}` : ''}</div></div></div>
    <p class="bd-desc">${esc(o.desc)}</p>
    <dl class="bd-facts">
      ${fact('You have', full(t.bag[o.id] ?? 0))}
      ${fact('Sells for', o.sellPrice ? `${coin(o.sellPrice * r.out.qty)}${r.out.qty > 1 ? ` <span class="muted">(${full(o.sellPrice)} each)</span>` : ''}` : '<span class="muted">No merchant buys it</span>')}
      ${inWorth ? fact('Inputs sell for', coin(inWorth)) : ''}
      ${o.shopPrice ? fact('Merchant price', coin(o.shopPrice * r.out.qty)) : ''}
      ${fact('Market', o.tradable ? 'Tradeable' : 'Account-bound')}
      ${limit ? fact('Limit', esc(limit)) : ''}
    </dl>
    ${useLine(o) ? `<div class="bd-sec"><h6>Used for</h6><p>${esc(useLine(o))}</p></div>` : ''}
    ${found ? `<div class="bd-sec"><h6>Also drops</h6><p>${esc(found)}</p></div>` : ''}
    <div class="bd-sec"><h6>Takes${q > 1 ? ` <span class="muted">for ${full(q)}</span>` : ''}</h6><ul class="cr-takes">${ins.join('')}</ul></div>
    <div class="bd-acts cr-acts">${can.n ? `${stepper(key, `How many ${o.name} to craft`)}<button class="btn primary small" data-act="craft" data-id="${r.id}" data-times="${q}">Craft${q > 1 ? ` ×${full(q)}` : ''}</button>`
      : `<p class="bad-text">${esc(can.why)}</p>`}</div>`;
}
// ---------------- bulk scrap ----------------
const SCRAP_Q = [10, 25, 50, 75, 90, 100];
const SCRAP_UPTO: [Grade | 'all', string][] = [['C', 'C only'], ['B', 'B and below'], ['A', 'A and below'], ['S', 'S and below'], ['all', 'Any grade']];
const SCRAP_KEEP = [0, 1, 2, 3, 5];

/**
 * Scrap relics in bulk: the kinds (all or any mix), the quality and grade they go up to, and how many of the best of
 * each kind to keep. Worn relics never go. The preview counts what goes of each kind as the choices change.
 */
async function bulkScrap() {
  const t = S.trainer!;
  const owned = new Map<RelicStat, number>();
  for (const r of t.relics) owned.set(r.stat, (owned.get(r.stat) ?? 0) + 1);
  const kinds = RELIC_STATS.map((d) => d.stat).filter((k) => owned.has(k));
  const start = relicFilter && owned.has(relicFilter as RelicStat) ? relicFilter : '';
  const opts = (d: Record<string, string>): BulkScrap => ({
    stats: new Set(kinds.filter((k) => d[`k:${k}`] === '1')),
    maxQ: SCRAP_Q.includes(Number(d.maxQ)) ? Number(d.maxQ) : 50,
    upTo: SCRAP_UPTO.some(([g]) => g === d.upTo) ? d.upTo as Grade | 'all' : 'B',
    keep: SCRAP_KEEP.includes(Number(d.keep)) ? Number(d.keep) : 1,
  });
  const name = (k: RelicStat) => `${RELIC_STATS.find((d) => d.stat === k)!.name}s`;
  const card = (k: RelicStat) => `<label class="pcard alp-c bk-sp"><input type="checkbox" name="k:${k}"${!start || start === k ? ' checked' : ''}><img class="relic-ic" src="${iconUrl(`relic-${k}`, 48)}" alt=""><span class="grow"><b>${esc(name(k))}</b><br><span class="muted" data-n="${k}"></span></span></label>`;
  let wired = false;
  const r = await openDialog({
    title: 'Scrap relics',
    body: `<div class="bk-opts">
        <label class="dlg-field"><span>Quality</span><select name="maxQ">${SCRAP_Q.map((q) => `<option value="${q}"${q === 50 ? ' selected' : ''}>${q === 100 ? 'Any quality' : `Q${q} and below`}</option>`).join('')}</select></label>
        <label class="dlg-field"><span>Grade</span><select name="upTo">${SCRAP_UPTO.map(([v, n]) => `<option value="${v}"${v === 'B' ? ' selected' : ''}>${n}</option>`).join('')}</select></label>
        <label class="dlg-field"><span>Keep the best</span><select name="keep">${SCRAP_KEEP.map((n) => `<option value="${n}"${n === 1 ? ' selected' : ''}>${n}</option>`).join('')}</select> <span class="sub">of each kind</span></label>
      </div>
      <div class="alp-head bk-head"><b>Relics</b><span class="muted" data-sp-n></span><button type="button" class="linkbtn" data-bk="all">All</button><button type="button" class="linkbtn" data-bk="none">None</button></div>
      <div class="picker alp bk-grid">${kinds.map(card).join('')}</div>
      <p class="fee rb-count" data-count></p>
      <p class="sub">Worn relics are never scrapped.</p>`,
    danger: true,
    actions: [{ label: 'Scrap', value: 'scrap', kind: 'danger' }],
    onInput: (root, d) => {
      if (!wired) {
        wired = true;
        root.addEventListener('click', (e) => {
          const b = (e.target as HTMLElement).closest<HTMLElement>('[data-bk]');
          if (!b) return;
          for (const box of root.querySelectorAll<HTMLInputElement>('.bk-sp input')) box.checked = b.dataset.bk === 'all';
          root.dispatchEvent(new Event('input', { bubbles: true }));
        });
      }
      const o = opts(d), list = bulkScrapList(t.relics, o);
      const would = new Map<RelicStat, number>();
      for (const x of bulkScrapList(t.relics, { ...o, stats: 'all' })) would.set(x.stat, (would.get(x.stat) ?? 0) + 1);
      for (const el of root.querySelectorAll<HTMLElement>('[data-n]')) {
        const k = el.dataset.n as RelicStat, on = d[`k:${k}`] === '1', n = would.get(k) ?? 0;
        el.textContent = on ? `${full(n)} of ${full(owned.get(k)!)} go` : `${full(owned.get(k)!)} · all kept`;
        el.closest('.alp-c')!.classList.toggle('on', on);
        el.closest('.alp-c')!.classList.toggle('zero', on && !n);
      }
      root.querySelector('[data-sp-n]')!.textContent = `${full(o.stats === 'all' ? kinds.length : o.stats.size)} of ${full(kinds.length)}`;
      const coins = list.reduce((a, x) => a + relicScrapCoins(x), 0);
      root.querySelector('[data-count]')!.textContent = list.length ? `${plural(list.length, 'relic')} · ${full(coins)} coins` : 'Nothing to scrap';
      const btn = [...root.querySelectorAll<HTMLButtonElement>('.actions button')].pop()!;
      btn.disabled = !list.length;
      btn.textContent = list.length ? `Scrap ${full(list.length)} for ${full(coins)} coins` : 'Scrap';
    },
  });
  if (r.value !== 'scrap') return;
  const list = bulkScrapList(t.relics, opts(r.data));
  if (!list.length) return;
  // A top relic going (Q90+ or graded S or better): one more look first.
  const good = list.filter((x) => x.quality >= 90 || ['S', 'S+'].includes(relicGrade(x) ?? ''));
  if (good.length) {
    const ok = await openDialog({ title: `Scrap ${plural(good.length, 'top relic')}?`, body: `<p>${good.map((x) => `${esc(relicName(x))} Q${x.quality}`).join(', ')} go with the rest. Can't be undone.</p>`, danger: true, actions: [{ label: `Scrap all ${full(list.length)}`, value: 'go', kind: 'danger' }] });
    if (!ok.value) return;
  }
  const coins = list.reduce((a, x) => a + relicScrapCoins(x), 0);
  send({ t: 'scrapRelics', relics: list.map((x) => x.id) });
  if (bagSel.startsWith('r:') && list.some((x) => x.id === bagSel.slice(2))) bagSel = '';
  toast(`Scrapped ${plural(list.length, 'relic')} for ${full(coins)} coins`);
}

/** A held item at the bench, in the detail panel: what it does, how its quality rolls, its pieces and where they drop. */
function forgeDetail(id: string) {
  const t = S.trainer!, d = HELD[id], piece = ITEM[heldPiece(id)];
  const have = t.bag[piece.id] ?? 0;
  const own = (t.held ?? []).filter((h) => h.item === id).length + [...S.creatures.values()].filter((c) => c.held?.item === id).length;
  const odds = (q: number) => `1 in ${full(Math.round(1 / qualityAtLeast(q)))}`;
  return `<div class="bd-head"><img class="bd-art" src="${iconUrl(id)}" alt=""><div><h4>${esc(d.name)}</h4>
      <div class="bd-kind">Held item · ${esc(SPECIES[d.boss]?.name ?? d.boss)}</div></div></div>
    <p class="bd-desc">${esc(d.lore)}</p>
    <dl class="bd-facts">
      ${fact('Does', esc(heldRange(id)))}
      ${fact('You own', full(own))}
      ${fact('Quality', `${QUALITY_MIN}-${QUALITY_MAX}%, rolled every forge`)}
      ${fact('Top rolls', `90%+ is ${odds(90)}<br>100% is ${odds(100)}`)}
    </dl>
    <div class="bd-sec"><h6>Used for</h6><p>Worn by one creature in PvP only: the Arena, Halls, friendly battles, tournaments and Trail trainers.</p></div>
    <div class="bd-sec"><h6>Takes</h6><ul class="cr-takes"><li class="${have >= PIECES_PER_HELD ? '' : 'short'}"><button class="cr-piece" data-act="bag-piece" data-id="${piece.id}" title="Open in the Bag"><img src="${iconUrl(piece.id)}" alt=""></button><div><b>${PIECES_PER_HELD} × ${esc(piece.name)}</b><span>${esc(fromLine(piece))}</span></div><em>${full(have)} / ${PIECES_PER_HELD}</em></li></ul></div>
    <div class="bd-acts cr-acts">${forgeElSelect(id)}${forgeButton(id, 'small')}</div>`;
}

// ---------------- events ----------------
function sell(id: string, n: number) {
  const it = ITEM[id], have = S.trainer!.bag[id] ?? 0;
  const q = Math.max(1, Math.min(have, Math.floor(n)));
  if (!it || !have || !it.sellPrice) return;
  const total = it.sellPrice * q;
  const done = () => { send({ t: 'sell', item: id, qty: q }); qty.delete(`sell:${id}`); };
  if (total <= 10_000) return done();
  void openDialog({ title: `Sell ${full(q)} ${it.name}?`, body: `<p>You get ${full(total)} coins (${full(it.sellPrice)} each).</p>`, actions: [{ label: `Sell for ${full(total)} coins`, value: 'sell', kind: 'primary' }] })
    .then((r) => { if (r.value) done(); });
}

export function bagShopClick(el: HTMLElement): boolean {
  const t = S.trainer!;
  const id = el.dataset.id ?? '';
  switch (el.dataset.act) {
    case 'bs-rail': rail = id as Rail; rerender(); return true;
    // "Buy more" in the Bag: the link opens the Merchant on the item's rail.
    case 'bs-more': rail = id as Rail; return true;
    case 'bs-dec': setQty(id, getQty(id) - 1); rerender(); return true;
    case 'bs-inc': setQty(id, getQty(id) + 1); rerender(); return true;
    case 'bs-max': setQty(id, maxFor(id)); rerender(); return true;
    case 'bs-buy': {
      const it = ITEM[id], q = getQty(`buy:${id}`);
      if (it?.shopPrice && t.coins >= priceOf(id) * q) send({ t: 'buy', item: id, qty: q });
      return true;
    }
    case 'craft': send({ t: 'craft', recipe: id, times: Number(el.dataset.times) || 1 }); qty.delete(`craft:${id}`); return true;
    case 'bs-sell': sell(id, getQty(`sell:${id}`)); return true;
    case 'use-charm': if (ITEM[id]?.boost && (t.bag[id] ?? 0) > 0) send({ t: 'useCharm', item: id }); return true;
    case 'bag-cat': bagCat = id; bagSel = ''; rerender(); return true;
    case 'bag-pick': bagSel = id; bagOpen = true; rerender(); return true;
    // A held item's pieces, from its detail at the bench: open them in the Treasures pocket.
    case 'bag-piece': bagSel = `i:${id}`; bagCat = 'held'; bagPocket = 'treasures'; bagOpen = true; go('#/bag/treasures'); return true;
    // A piece's "Forges into": the item at the bench.
    case 'bag-forge': bagSel = `h:${id}`; bagOpen = true; if (route().sub === 'craft') rerender(); else go('#/bag/craft'); return true;
    // A "Crafts into" line in an item's detail opens that recipe on the bench.
    case 'bag-recipe': bagSel = `c:${id}`; bagOpen = true; if (route().sub === 'craft') rerender(); else go('#/bag/craft'); return true;
    // The Altar pocket: the Altar dialog over storage, as from Kinhall.
    case 'bag-altar': go('#/creatures'); void altar(); return true;
    case 'bag-close': bagOpen = false; rerender(); return true;
    case 'bs-bulk-scrap': void bulkScrap(); return true;
    case 'bs-scrap': {
      const r = t.relics.find((x) => x.id === id);
      if (!r) return true;
      const coins = 50 + r.quality * 5;
      if (r.quality < 40) { send({ t: 'scrapRelic', relic: id }); return true; }
      void openDialog({ title: `Scrap ${relicName(r)}?`, body: `<p>Q${r.quality} · ${esc(relicDesc(r))}. You get ${full(coins)} coins; the relic is gone for good.</p>`, danger: true, actions: [{ label: `Scrap for ${full(coins)} coins`, value: 'scrap', kind: 'danger' }] })
        .then((x) => { if (x.value) send({ t: 'scrapRelic', relic: id }); });
      return true;
    }
  }
  return false;
}

export function bagShopChange(el: HTMLInputElement | HTMLSelectElement): boolean {
  if (el.dataset.bsQty !== undefined) { setQty(el.dataset.bsQty, Number(el.value)); return true; }
  if (el.dataset.relicFilter !== undefined) { relicFilter = el.value; return true; }
  return false;
}
